/**
 * Multi-select tag filter dropdown — same look as TagFilterDropdown, but any
 * number of tags can be active at once (OR semantics). The popover stays open
 * while you toggle. For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';

import { cn } from '@/lib/utils';

import { NO_TAGS_VALUE } from '../TagFilterDropdown';
import { MultiTagOption } from './MultiTagOption';
import { MultiTagSelectAllRow } from './MultiTagSelectAllRow';
import { MultiTagTrigger } from './MultiTagTrigger';

export interface MultiTagFilterDropdownProps {
  values: string[];
  options: string[];
  tagColors?: Record<string, string>;
  onChange: (values: string[]) => void;
  /** Trigger text when nothing is selected. */
  placeholder?: string;
  /** Label for the "has no tag" option. Default: "No tags". */
  noneLabel?: string;
  /** Compact trigger height. Default: h-9 */
  compact?: boolean;
}

export const MultiTagFilterDropdown: FC<MultiTagFilterDropdownProps> = ({
  values,
  options,
  tagColors,
  onChange,
  placeholder = 'All tags',
  noneLabel = 'No tags',
  compact = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? options.filter((tag) => tag.toLowerCase().includes(q)) : options;
  }, [options, search]);

  const selected = useMemo(() => new Set(values), [values]);
  const showNone = !search.trim() || noneLabel.toLowerCase().includes(search.toLowerCase());

  const toggle = (tag: string) =>
    onChange(selected.has(tag) ? values.filter((v) => v !== tag) : [...values, tag]);

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <Popover.Trigger asChild>
        <MultiTagTrigger
          values={values}
          tagColors={tagColors}
          placeholder={placeholder}
          noneLabel={noneLabel}
          compact={compact}
          open={open}
        />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className={cn(
            'z-[250] min-w-[200px] max-w-[min(300px,70vw)] rounded border border-border bg-popover p-0 text-popover-foreground shadow-lg',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <div className="border-b border-border p-2">
            <div className="flex items-center gap-2 rounded-md border border-input bg-background px-2 py-1.5">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                type="search"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                autoFocus
                autoComplete="off"
              />
            </div>
          </div>
          <MultiTagSelectAllRow
            values={values}
            visibleOptions={filteredOptions}
            onChange={onChange}
          />
          <div role="listbox" aria-multiselectable className="max-h-[260px] overflow-y-auto py-1">
            <button
              type="button"
              role="option"
              aria-selected={values.length === 0}
              className={cn(
                'flex w-full items-center justify-between px-3 py-2 text-left text-sm font-medium transition-colors',
                'hover:bg-accent hover:text-accent-foreground',
                values.length === 0 && 'bg-accent text-accent-foreground',
              )}
              onClick={() => onChange([])}
            >
              {placeholder}
              {values.length > 0 && (
                <span className="text-xs font-normal text-muted-foreground">clear</span>
              )}
            </button>
            {filteredOptions.map((tag) => (
              <MultiTagOption
                key={tag}
                tag={tag}
                isSelected={selected.has(tag)}
                onToggle={() => toggle(tag)}
                tagColors={tagColors}
              />
            ))}
            {showNone && (
              <MultiTagOption
                tag={NO_TAGS_VALUE}
                label={noneLabel}
                isSelected={selected.has(NO_TAGS_VALUE)}
                onToggle={() => toggle(NO_TAGS_VALUE)}
                muted
              />
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
